/**
 * Advertising data model. Slots are defined by track segments; campaigns and
 * creatives come from a provider (placeholder now, backend later).
 * See docs/advertising/AD_SYSTEM.md.
 */
export const AdSlotType = {
  FenceLeft: 'AD_FENCE_LEFT',
  FenceRight: 'AD_FENCE_RIGHT',
  ContainerLeft: 'AD_CONTAINER_LEFT',
  ContainerRight: 'AD_CONTAINER_RIGHT',
  ScaffoldHigh: 'AD_SCAFFOLD_HIGH',
  BuildingBanner: 'AD_BUILDING_BANNER',
  /** Large scaffold wrap on the shell building behind a closed junction end. */
  BuildingWrap: 'AD_BUILDING_WRAP',
  CraneBanner: 'AD_CRANE_BANNER',
  /** Banner of the ad gantry spanning the road on open straights (9.9 × 2.5 m, 4:1 creative, far visible). */
  GantryBanner: 'AD_GANTRY_BANNER',
} as const;
export type AdSlotType = (typeof AdSlotType)[keyof typeof AdSlotType];

/** Physical format of a slot – creatives are produced per format, not per slot. */
export interface AdFormat {
  /** Width / height ratio of the visible surface. */
  readonly aspectRatio: number;
  /** Recommended creative resolution in pixels. */
  readonly recommendedPixels: readonly [number, number];
}

export const AD_FORMATS: Readonly<Record<AdSlotType, AdFormat>> = {
  AD_FENCE_LEFT: { aspectRatio: 4, recommendedPixels: [2048, 512] },
  AD_FENCE_RIGHT: { aspectRatio: 4, recommendedPixels: [2048, 512] },
  AD_CONTAINER_LEFT: { aspectRatio: 2.4, recommendedPixels: [1229, 512] },
  AD_CONTAINER_RIGHT: { aspectRatio: 2.4, recommendedPixels: [1229, 512] },
  AD_SCAFFOLD_HIGH: { aspectRatio: 3, recommendedPixels: [1536, 512] },
  AD_BUILDING_BANNER: { aspectRatio: 0.5, recommendedPixels: [512, 1024] },
  AD_BUILDING_WRAP: { aspectRatio: 1.4, recommendedPixels: [1434, 1024] },
  AD_CRANE_BANNER: { aspectRatio: 5, recommendedPixels: [2560, 512] },
  // 9.9 × 2.5 m (4:1 creative, 1 % squeeze) – ≈ 256 px per metre, crisp when the runner passes right under it
  AD_GANTRY_BANNER: { aspectRatio: 4, recommendedPixels: [2560, 640] },
};

/**
 * Texture window that shows the centre of an image (ratio `source`) in a surface of ratio `target` without stretching:
 * a wider image loses its sides, a taller one top and bottom.
 */
export function coverCrop(source: number, target: number): { uScale: number; vScale: number; uOffset: number; vOffset: number } {
  if (!(source > 0) || !(target > 0) || Math.abs(source - target) < 1e-3) return { uScale: 1, vScale: 1, uOffset: 0, vOffset: 0 };
  if (source > target) {
    const uScale = target / source;
    return { uScale, vScale: 1, uOffset: (1 - uScale) / 2, vOffset: 0 };
  }
  const vScale = source / target;
  return { uScale: 1, vScale, uOffset: 0, vOffset: (1 - vScale) / 2 };
}

export interface AdCreative {
  readonly creativeId: string;
  readonly campaignId: string;
  /** Image URL (PNG/JPG/WebP/KTX2) – null for generated placeholders. */
  readonly textureUrl: string | null;
  readonly slotTypes: readonly AdSlotType[];
  /** Fallback/placeholder text (also used as alt text in reports). */
  readonly label: string;
  readonly backgroundColor?: string;
  readonly textColor?: string;
  /** Placeholder only: prints the slot type under the label (ad sales "your ad here" boards). */
  readonly showSlotType?: boolean;
  /**
   * Width / height of the image when it was made for another format (a sponsor's poster on a surface it was not booked
   * for) – the surface then shows its centre in its own ratio (coverCrop), never stretched.
   */
  readonly sourceAspect?: number;
}

export interface AdCampaign {
  readonly campaignId: string;
  readonly companyId: string;
  readonly companyName: string;
  /** ISO 8601 date-time (inclusive). */
  readonly startDate: string;
  /** ISO 8601 date-time (inclusive). */
  readonly endDate: string;
  /** Relative delivery weight between active campaigns. */
  readonly weight: number;
  readonly slotTypes: readonly AdSlotType[];
  readonly creatives: readonly AdCreative[];
  /** Optional cap – the provider stops delivering after this many impressions. */
  readonly maxImpressions?: number;
}

/** One counted impression – will later be batched to the backend. */
export interface AdImpression {
  readonly slotInstanceId: string;
  readonly slotType: AdSlotType;
  readonly campaignId: string;
  readonly companyId: string;
  readonly creativeId: string;
  /** Continuous visible time when the impression was counted (seconds). */
  readonly visibleSeconds: number;
  /** Largest screen-height share the slot covered during the visibility window. */
  readonly maxScreenFraction: number;
  readonly runDistance: number;
  readonly timestamp: string;
}
